import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { DataGrid } from '../components/DataGrid';
import { Robot, MagnifyingGlassPlus, Plus, X } from '@phosphor-icons/react';

const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong';
const TYPES = ['service_account', 'api_key', 'token', 'secret', 'certificate', 'bot', 'ai_agent', 'application', 'workload', 'automation'];
const RiskChip = ({ level }) => <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${level === 'high' ? 'bg-rose-100 text-rose-700' : level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{level}</span>;
const StatusChip = ({ s }) => <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${s === 'active' ? 'bg-slate-900 text-white' : s === 'disabled' ? 'bg-slate-200 text-slate-600' : 'bg-rose-100 text-rose-700'}`}>{s}</span>;

const Stat = ({ label, value, tone, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
    <p className="font-mono-label text-[10px] uppercase text-slate-400">{label}</p>
    <p className={`font-display text-2xl font-extrabold ${tone || ''}`}>{value}</p>
  </div>
);

const CreateForm = ({ onDone }) => {
  const [f, setF] = useState({ name: '', nhi_type: 'service_account', owner_email: '', description: '', privileged: false, expires_at: '', rotation_days: '' });
  const create = async () => {
    try {
      await api.post('/nhi', { ...f, owner_email: f.owner_email || null, expires_at: f.expires_at || null, rotation_days: f.rotation_days ? +f.rotation_days : null });
      toast.success('Identity registered'); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = 'border border-slate-200 rounded-md px-2.5 py-2 text-sm focus:border-orange-500 focus:outline-none';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="nhi-create-form">
      <p className="font-display font-bold mb-3">Register a non-human identity</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <input data-testid="nhi-f-name" className={inp} placeholder="Name (e.g. payroll-export bot)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <select data-testid="nhi-f-type" className={inp} value={f.nhi_type} onChange={(e) => setF({ ...f, nhi_type: e.target.value })}>{TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}</select>
        <input data-testid="nhi-f-owner" className={inp} placeholder="Owner email (blank = orphaned)" value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} />
        <input className={`${inp} sm:col-span-3`} placeholder="What is it for?" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.privileged} onChange={(e) => setF({ ...f, privileged: e.target.checked })} className="accent-orange-600" data-testid="nhi-f-priv" />Privileged access</label>
        <input type="date" className={inp} title="Expiry date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} />
        <input type="number" className={inp} placeholder="Rotation period (days)" value={f.rotation_days} onChange={(e) => setF({ ...f, rotation_days: e.target.value })} />
      </div>
      <button data-testid="nhi-create-btn" disabled={!f.name.trim()} onClick={create} className="mt-3 bg-slate-900 hover:bg-slate-800 text-white text-sm rounded-md px-4 py-2 disabled:opacity-50">Register identity</button>
    </div>
  );
};

const Drawer = ({ row, onClose, onAction }) => (
  <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40" onClick={onClose}>
    <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="nhi-drawer">
      <div className="flex items-start justify-between mb-2">
        <div><p className="font-display font-extrabold text-lg">{row.name}</p><p className="text-xs text-slate-400 capitalize">{row.nhi_type.replace('_', ' ')} · {row.source}</p></div>
        <button onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>
      <div className="flex items-center gap-2 mb-4"><StatusChip s={row.status} /><RiskChip level={row.risk_level} /><span className="text-xs text-slate-500">risk score {row.risk_score}</span></div>
      {row.risk_reasons?.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-3 mb-4">
          <p className="font-mono-label text-[10px] uppercase text-amber-600 mb-1">Risk factors</p>
          {row.risk_reasons.map((r) => <p key={r} className="text-sm text-amber-800">• {r}</p>)}
        </div>)}
      <div className="text-sm space-y-1.5 mb-5">
        <p><span className="text-slate-400">Owner:</span> {row.owner_email || <span className="text-rose-500">none (orphaned)</span>}</p>
        <p><span className="text-slate-400">Purpose:</span> {row.description || '-'}</p>
        <p><span className="text-slate-400">Privileged:</span> {row.privileged ? 'Yes' : 'No'}</p>
        <p><span className="text-slate-400">Expires:</span> {row.expires_at || 'never'}</p>
        <p><span className="text-slate-400">Rotation:</span> {row.rotation_days ? `every ${row.rotation_days}d, last ${row.last_rotated_at ? row.last_rotated_at.slice(0, 10) : 'never'}` : 'not required'}</p>
        <p><span className="text-slate-400">Last used:</span> {row.last_used_at ? row.last_used_at.slice(0, 10) : 'never recorded'}</p>
        <p><span className="text-slate-400">Last review:</span> {row.last_reviewed_at ? `${row.last_reviewed_at.slice(0, 10)} by ${row.last_reviewed_by}` : 'never'}</p>
      </div>
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Lifecycle actions</p>
      <div className="flex flex-wrap gap-2">
        {[['rotate', 'Mark rotated'], ['review', 'Mark reviewed'], row.status === 'active' ? ['disable', 'Disable'] : ['enable', 'Enable'], ['terminate', 'Terminate']].map(([a, label]) => (
          <button key={a} data-testid={`nhi-action-${a}`} onClick={() => onAction(row.id, a)}
            className={`text-sm rounded-md px-3 py-1.5 border ${a === 'terminate' ? 'border-rose-300 text-rose-600 hover:bg-rose-50' : 'border-slate-200 hover:border-orange-500'}`}>{label}</button>))}
      </div>
    </div>
  </div>
);

export default function NHI() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [active, setActive] = useState(null);
  const { data: rows, isLoading } = useQuery({ queryKey: ['nhi'], queryFn: () => api.get('/nhi').then((r) => r.data) });
  const { data: dash } = useQuery({ queryKey: ['nhi-dash'], queryFn: () => api.get('/nhi/dashboard').then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['nhi'] }); qc.invalidateQueries({ queryKey: ['nhi-dash'] }); };
  const action = async (id, a) => {
    const reason = a === 'terminate' ? window.prompt('Reason for termination:') : undefined;
    if (a === 'terminate' && !reason) return;
    try { const { data } = await api.post(`/nhi/${id}/lifecycle`, { action: a, reason }); toast.success(`${a} done`); setActive(data); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const bulk = (a) => async (ids) => {
    try { const { data } = await api.post('/nhi/bulk', { ids, action: a }); toast.success(`${data.done} updated${data.errors.length ? `, ${data.errors.length} failed` : ''}`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const discover = async () => {
    try { const { data } = await api.post('/nhi/discover'); toast.success(`Discovery: ${data.scanned} scanned, ${data.created} new identities registered`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const inlineEdit = async (row, key, value) => {
    try { await api.patch(`/nhi/${row.id}`, { [key]: value }); toast.success('Saved'); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="nhi-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><Robot size={26} className="text-orange-600" />Non-Human Identities</h1>
          <p className="text-sm text-slate-500">Service accounts, API keys, tokens, bots and AI agents - with lifecycle and risk.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="nhi-discover-btn" onClick={discover} className="border border-slate-200 rounded-md px-3 py-2 text-sm hover:border-orange-500 bg-white flex items-center gap-1.5"><MagnifyingGlassPlus size={15} />Discover platform identities</button>
          <button data-testid="nhi-new-btn" onClick={() => setShowCreate(!showCreate)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-md px-3 py-2 text-sm flex items-center gap-1.5"><Plus size={15} />Register</button>
        </div>
      </div>
      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label="Total" value={dash.total} testid="nhi-stat-total" />
          <Stat label="Active" value={dash.active} testid="nhi-stat-active" />
          <Stat label="Privileged" value={dash.privileged} tone="text-orange-600" testid="nhi-stat-priv" />
          <Stat label="Orphaned" value={dash.orphaned} tone={dash.orphaned ? 'text-rose-600' : ''} testid="nhi-stat-orphaned" />
          <Stat label="Expiring 30d" value={dash.expiring_30d} tone={dash.expiring_30d ? 'text-amber-600' : ''} testid="nhi-stat-expiring" />
          <Stat label="High risk" value={dash.high_risk} tone={dash.high_risk ? 'text-rose-600' : ''} testid="nhi-stat-highrisk" />
        </div>)}
      {showCreate && <CreateForm onDone={() => { setShowCreate(false); refresh(); }} />}
      <DataGrid gridId="nhi" rows={rows || []} loading={isLoading} onRowClick={setActive} onEdit={inlineEdit} exportName="nhi_identities"
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'nhi_type', label: 'Type', render: (r) => <span className="capitalize">{r.nhi_type.replace('_', ' ')}</span> },
          { key: 'owner_email', label: 'Owner', editable: true, render: (r) => r.owner_email || <span className="text-rose-500 text-xs">orphaned</span> },
          { key: 'status', label: 'Status', render: (r) => <StatusChip s={r.status} /> },
          { key: 'risk_level', label: 'Risk', render: (r) => <RiskChip level={r.risk_level} /> },
          { key: 'risk_score', label: 'Score' },
          { key: 'privileged', label: 'Privileged', render: (r) => (r.privileged ? 'Yes' : 'No') },
          { key: 'expires_at', label: 'Expires', render: (r) => r.expires_at ? r.expires_at.slice(0, 10) : '-' },
          { key: 'last_used_at', label: 'Last used', hidden: true, render: (r) => r.last_used_at ? r.last_used_at.slice(0, 10) : 'never' },
          { key: 'source', label: 'Source', hidden: true },
          { key: 'description', label: 'Purpose', hidden: true, editable: true },
        ]}
        filters={[
          { key: 'nhi_type', label: 'Type', options: TYPES },
          { key: 'status', label: 'Status', options: ['active', 'disabled', 'terminated'] },
          { key: 'risk_level', label: 'Risk', options: ['low', 'medium', 'high'] },
        ]}
        bulkActions={[
          { key: 'review', label: 'Mark reviewed', run: bulk('review') },
          { key: 'rotate', label: 'Mark rotated', run: bulk('rotate') },
          { key: 'disable', label: 'Disable', run: bulk('disable') },
          { key: 'terminate', label: 'Terminate', tone: 'danger', run: bulk('terminate') },
        ]}
        emptyText="No machine identities yet - click Discover to scan the platform, or Register one manually." />
      {active && <Drawer row={(rows || []).find((r) => r.id === active.id) || active} onClose={() => setActive(null)} onAction={action} />}
    </div>
  );
}
